import {ChangeDetectorRef, Component, HostListener, Input} from '@angular/core';
import {UploadService} from '@shared/services/upload.service';
import {Subscription} from 'rxjs';
import * as Sentry from '@sentry/angular-ivy';
import {Attachment, Blob, FileUploadInput, Image} from '@paperlessio/sdk/api/models';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {first} from 'rxjs/operators';
import {ToastService} from '@paperlessio/sdk/api/util';

// noinspection AngularMissingOrInvalidDeclarationInModule
@Component({
  selector: 'app-upload-block',
  template: ''
})
export class UploadBlockComponent {
  @Input() block: Attachment | Image | FileUploadInput;
  @Input() slug: string;

  public previewEnabled = false;
  public uploadingBlob: Blob;

  protected errorTranslationKey = 'base.file_picker.defaults';
  protected subs = new Subscription();
  protected uploadSub: Subscription;

  @HostListener('window:beforeunload', []) canDeactivate() {
    return !this.uploadService?.isUploading();
  }

  constructor(
    protected uploadService: UploadService,
    protected blockMutationService: BlockMutationService,
    protected toast: ToastService,
    protected cdr: ChangeDetectorRef,
  ) { }

  init() {
    this.subs.add(
      this.uploadService.progress.subscribe(blob => {
        if (this.slug === blob.uploadIdentifier) {
          this.uploadingBlob = blob;
          this.previewEnabled = true;
          this.cdr.markForCheck();
        }
      })
    );
  }

  destroy() {
    this.subs.unsubscribe();
    this.resetUpload();
  }

  public deleteFile(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
    this.reset();
  }

  public onFile(file: File) {
    this.resetUpload();
    this.upload(file);
  }

  protected upload(file: File): void {
    if (!!file) {
      this.uploadingBlob = Blob.forUpload(file, this.slug);
      this.uploadSub = this.uploadService.upload(this.uploadingBlob)
        .subscribe(uploadedBlob => {
          this.onUploaded(uploadedBlob);
        }, error => {
          this.reset();
          this.toast.error(this.errorTranslationKey + '.errors.upload_error');
          Sentry.captureException(error);
        });
    }
  }

  protected onUploaded(blob: Blob) {
    // we don't replace the blob, we just update the signed_id to prevent flickering
    this.block.file.signed_id = blob.signed_id;
    this.blockMutationService.updateBlock(this.block, true)
      .pipe(first())
      .subscribe();
    this.cdr.markForCheck();
  }

  protected reset() {
    this.block.reset();
    this.resetUpload();
    this.blockMutationService.updateBlock(this.block, true)
      .pipe(first())
      .subscribe();
    this.cdr.markForCheck();
  }

  protected resetUpload() {
    if (this.uploadSub) {
      this.uploadService.resetUpload(this.uploadingBlob);
      this.uploadSub.unsubscribe();
    }
  }
}
